import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { PlusIcon } from "app-v2";
import { ColumnDef } from "@tanstack/react-table";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import EventDataTable from "../../banker/events/EventsDataTable";
import type { AssetCardProps } from "../../buyer/assets/AssetCard";
import { getAssetTransferAssets } from "services";

interface ViewAssetsProps {
  title?: string;
  showCreateButton?: boolean;
  onCreateClick?: () => void;
}

type AssetRow = AssetCardProps;

function mapAuctionStatus(raw: string | undefined): AssetCardProps["status"] {
  const s = String(raw || "").toLowerCase();
  if (s.includes("live")) return "LIVE";
  if (s.includes("end") || s.includes("completed")) return "AUCTION_ENDED";
  return "UPCOMING";
}

/** Parses Postgres / ISO timestamps for display in IST */
function formatIstLabel(value: string | undefined): string {
  if (!value) return "—";
  try {
    const sql = DateTime.fromSQL(String(value), { zone: "utc" });
    if (sql.isValid) {
      return sql
        .setZone("Asia/Kolkata")
        .toFormat("dd LLLL yyyy hh:mm a '(IST)'");
    }
    const iso = DateTime.fromISO(String(value), { setZone: true });
    if (iso.isValid) {
      return iso
        .setZone("Asia/Kolkata")
        .toFormat("dd LLLL yyyy hh:mm a '(IST)'");
    }
    return String(value);
  } catch {
    return String(value);
  }
}

/** e.g. "Sector 62, Noida, Uttar Pradesh" → city + state */
function parseSecuredLocation(raw: string | undefined): {
  location: string;
  state: string;
} {
  if (!raw) return { location: "", state: "" };
  const parts = raw
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    return {
      location: parts[parts.length - 2] || parts[0],
      state: (parts[parts.length - 1] || "").toLowerCase(),
    };
  }
  return { location: raw, state: "" };
}

/**
 * Maps `asset-transfer-assets` rows (flat columns) to table fields.
 * Falls back to nested `meta.asset_data` when present.
 */
function mapAssetTransferRecordToRow(record: any): AssetRow {
  const meta =
    record?.meta && typeof record.meta === "object" ? record.meta : {};
  const nested = meta?.asset_data || record?.asset_data || {};

  const statusRaw =
    record?.status ?? nested?.status ?? meta?.status ?? "draft";

  const listingId =
    record?.listing_id ?? nested?.listing_id ?? record?.uuid ?? "—";

  const title =
    record?.secured_asset_description ??
    nested?.secured_asset_description ??
    record?.borrower_name ??
    nested?.borrower_name ??
    "Asset";

  const { location: locFromAddr, state: stateFromAddr } =
    parseSecuredLocation(
      record?.secured_asset_location ?? nested?.secured_asset_location,
    );

  const geo =
    record?.geographical_spread ?? nested?.geographical_spread ?? "";

  const location = locFromAddr || geo || "";
  const state =
    stateFromAddr || (geo ? String(geo).toLowerCase() : "");

  const reservePrice = String(
    record?.sanction_amount ?? nested?.sanction_amount ?? "",
  );

  const emdAmount =
    record?.emd_amount != null
      ? String(record.emd_amount)
      : nested?.emd_amount != null
        ? String(nested.emd_amount)
        : record?.total_outstanding != null
          ? String(record.total_outstanding)
          : "—";

  const biddingStart =
    record?.bidding_start_at ??
    nested?.bidding_start_at ??
    record?.auction_start_at;
  const biddingEnd =
    record?.bidding_end_at ??
    nested?.bidding_end_at ??
    record?.auction_end_at;

  return {
    status: mapAuctionStatus(statusRaw),
    listingId,
    title,
    location,
    state,
    reservePrice,
    emdAmount,
    biddingStartLabel: biddingStart
      ? formatIstLabel(biddingStart)
      : "—",
    biddingEndLabel: biddingEnd ? formatIstLabel(biddingEnd) : "—",
  };
}

function teamRowsToFilterIdentifiers(teams: unknown): string[] {
  if (!Array.isArray(teams) || teams.length === 0) return [];
  const ids = new Set<string>();
  for (const t of teams as Record<string, unknown>[]) {
    if (t?.name) ids.add(String(t.name));
    else if (t?.slug) ids.add(String(t.slug));
    else if (t?.uuid) ids.add(String(t.uuid));
  }
  return Array.from(ids);
}

const columns: ColumnDef<AssetRow>[] = [
  {
    accessorKey: "listingId",
    header: "Asset ID",
    cell: ({ row }) => (
      <span className="text-xs font-medium text-[#f5761a]">
        {row.original.listingId}
      </span>
    ),
  },
  {
    accessorKey: "title",
    header: "Asset Name",
    cell: ({ row }) => (
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-semibold text-[#111]">
          {row.original.title}
        </span>
        <span className="text-[11px] text-[#777] capitalize">
          {row.original.location}, {row.original.state}
        </span>
      </div>
    ),
  },
  {
    accessorKey: "reservePrice",
    header: "Reserve Price",
    cell: ({ row }) => (
      <span className="text-xs font-semibold text-[#111]">
        ₹ {row.original.reservePrice}
      </span>
    ),
  },
  {
    accessorKey: "emdAmount",
    header: "EMD Amount",
    cell: ({ row }) => (
      <span className="text-xs font-semibold text-[#111]">
        ₹ {row.original.emdAmount}
      </span>
    ),
  },
  {
    accessorKey: "biddingStartLabel",
    header: "Bidding Start",
    cell: ({ row }) => (
      <span className="text-[11px] text-[#444]">
        {row.original.biddingStartLabel}
      </span>
    ),
  },
  {
    accessorKey: "biddingEndLabel",
    header: "Bidding End",
    cell: ({ row }) => (
      <span className="text-[11px] text-[#444]">
        {row.original.biddingEndLabel}
      </span>
    ),
  },
];

const ViewAssets: React.FC<ViewAssetsProps> = ({
  title = "Assets List",
  showCreateButton = false,
  onCreateClick,
}) => {
  const router = useRouter();
  const teams = useSelector((state: any) => state.sellerAuth?.teams);
  const teamIdentifiers = useMemo(
    () => teamRowsToFilterIdentifiers(teams),
    [teams],
  );

  const [rows, setRows] = useState<AssetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (teamIdentifiers.length === 0) {
        setRows([]);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res: any = await getAssetTransferAssets({
          teamIdentifiers,
          page: 1,
          perPage: 10,
        });
        const raw = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
        const list = Array.isArray(raw) ? raw : [];
        if (!cancelled) {
          setRows(list.map(mapAssetTransferRecordToRow));
        }
      } catch (e) {
        console.error("getAssetTransferAssets failed", e);
        if (!cancelled) {
          setRows([]);
          setError("Could not load assets. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [teamIdentifiers]);

  return (
    <div className="px-4 lg:px-8 py-6">
      <div className="flex justify-between items-center">
        <h1 className="text-lg font-openSans font-normal text-bankerColor">
          {title}
        </h1>
        {showCreateButton && (
          <button
            type="button"
            onClick={
              onCreateClick ?? (() => {
                router.push("/assets/create");
              })
            }
            className="flex justify-center items-center w-[140px] gap-1 bg-black text-white text-sm px-2 py-3 rounded-[12px]"
          >
            <PlusIcon />
            Create Asset
          </button>
        )}
      </div>
      {teamIdentifiers.length === 0 && !loading && (
        <p className="mt-3 text-sm text-[#777]">
          No team assets linked to your account. Sign in again after team access
          is assigned.
        </p>
      )}
      {error && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      <div className="mt-4 border-2 border-gray-100 rounded-xl bg-white">
        <EventDataTable<AssetRow>
          data={rows}
          columns={columns}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default ViewAssets;
